import { Component, Input, ViewChild, OnChanges, OnInit, SimpleChanges, AfterViewInit, inject, ChangeDetectionStrategy } from '@angular/core';
import { StateOfChargeMeasurementPoint, VehicleStateOfChargeData } from 'src/app/modules/shared/models';

import { ChartComponent, ApexAxisChartSeries, ApexChart, ApexXAxis, ApexDataLabels, ApexTitleSubtitle, ApexStroke, ApexGrid, ApexYAxis, ApexTooltip, ApexLegend, NgApexchartsModule } from 'ng-apexcharts';
import { LoggingService } from 'src/app/modules/shared/services/logging.service';
import { VehicleInfoService } from 'src/app/modules/core/services/vehicle-info.service';
import { firstValueFrom } from 'rxjs';
import { TranslateModule } from '@ngx-translate/core';
import { FeedbackTextComponent } from '../../../modules/shared/components/feedback-text/feedback-text.component';
import { SpinnerComponent } from '../../../modules/shared/components/spinner/spinner.component';


export interface ChartOptions {
    series: ApexAxisChartSeries;
    chart: ApexChart;
    xaxis: ApexXAxis;
    yaxis: ApexYAxis;
    dataLabels: ApexDataLabels;
    grid: ApexGrid;
    stroke: ApexStroke;
    title: ApexTitleSubtitle;
    tooltip: ApexTooltip;
    colors: any[];
    legend: ApexLegend;
}

@Component({
    selector: 'app-state-of-charge-history',
    templateUrl: './state-of-charge-history.component.html',
    styleUrls: ['./state-of-charge-history.component.scss'],
    imports: [
        NgApexchartsModule,
        SpinnerComponent,
        FeedbackTextComponent,
        TranslateModule,
    ],
    changeDetection: ChangeDetectionStrategy.Eager
})
export class StateOfChargeHistoryComponent implements OnInit, OnChanges, AfterViewInit {
    private readonly _historySeriesName = 'SoC history';
    private readonly _prognosisSeriesName = 'SoC prognosis';

    private _logginService = inject(LoggingService);
    private _vehicleInfoService = inject(VehicleInfoService);

    @Input() vehicleInventoryNumber: string;
    @ViewChild('chart', { static: false }) chart: ChartComponent;
    public chartOptions: Partial<ChartOptions>;
    public stateOfChargeData: VehicleStateOfChargeData = null;
    public isLoadingData = false;


    constructor() {
        this._setChartOptions();
    }

    ngOnInit(): void {
        this._fetchChartData();
    }

    ngOnChanges(simpleChanges: SimpleChanges) {
        if (simpleChanges.vehicleInventoryNumber) {
            this._fetchChartData();
        }
    }

    ngAfterViewInit() {
        // Needed for the rendering of the chart to work the first time
        // Without it the chart has no data
        setTimeout(() => {
            this._renderChartData();
        }, 0);
    }

    protected getFormattedDataToChart(stateOfChargeData: VehicleStateOfChargeData) {
        return [
            {
                name: this._historySeriesName,
                data: this._formatChartSeriesData(stateOfChargeData?.history),
            },
            {
                name: this._prognosisSeriesName,
                data: this._formatChartSeriesData(stateOfChargeData?.prognosis),
            },
        ];
    }

    private async _getVehicleStateOfChargeData(inventoryNumber: string): Promise<VehicleStateOfChargeData> {
        try {
            return await firstValueFrom(this._vehicleInfoService.getVehicleStateOfChargeData(inventoryNumber)
        ) ?? new VehicleStateOfChargeData({
            history: [],
            prognosis: [],
        });
        } catch (error) {
            return new VehicleStateOfChargeData({
                history: [],
                prognosis: [],
            });
        }
    }

    private _renderChartData() {
        if (
            !this.stateOfChargeData ||
            this._hasNoStateOfChargeData(this.stateOfChargeData) ||
            !this.chart
        ) {
            return;
        }

        try {
            this.chart.updateSeries(
                this.getFormattedDataToChart(this.stateOfChargeData)
            );
            this.chart.updateOptions(
                this._getChartXAxisRange(),
                false,
                false,
                true
            );
            this.chart.showSeries(this._historySeriesName);

            if ((this.stateOfChargeData.prognosis ?? []).length > 0) {
                this.chart.showSeries(this._prognosisSeriesName);
            } else {
                this.chart.hideSeries(this._prognosisSeriesName);
            }
        } catch {
            this._logginService.logTrace('Unable to render state of charge graph');
        }
    }

    private async _fetchChartData() {
        if (!this.vehicleInventoryNumber || this.isLoadingData) {
            return;
        }
        this.isLoadingData = true;
        this.chart?.hideSeries(this._historySeriesName);
        this.chart?.hideSeries(this._prognosisSeriesName);
        this.stateOfChargeData = await this._getVehicleStateOfChargeData(this.vehicleInventoryNumber);
        this.isLoadingData = false;
        this._renderChartData();
    }

    private _setChartOptions() {
        this.chartOptions = {
            series: [
                {
                    name: this._historySeriesName,
                    data: [],
                },
                {
                    name: this._prognosisSeriesName,
                    data: [],
                },
            ],
            chart: {
                height: 350,
                type: 'line',
                zoom: {
                    enabled: false,
                },
                toolbar: {
                    show: false,
                },
            },
            dataLabels: {
                enabled: false,
            },
            stroke: {
                curve: 'straight',
                width: [4, 2],
                colors: ['#0078D7', '#757575'],
            },
            xaxis: {
                type: 'datetime',
                labels: {
                    format: 'HH:mm',
                    datetimeUTC: false,
                },
                tooltip: {
                    enabled: false,
                },
            },
            yaxis: {
                labels: {
                    formatter: (value) => value + '%',
                },
                min: 0,
                max: 100,
            },
            grid: {
                row: {
                    colors: ['#f3f3f3', 'transparent'],
                    opacity: 0.5,
                },
            },
            colors: ['#0078D7', '#757575'],
            tooltip: {
                shared: true,
                intersect: false,
                x: {
                    format: 'HH:mm',
                },
            },
            legend: {
                onItemClick: {
                    toggleDataSeries: false,
                },
                onItemHover: {
                    highlightDataSeries: false,
                },
            },
        };
    }

    private _formatChartSeriesData(socs: StateOfChargeMeasurementPoint[]) {
        return (socs ?? []).map(soc => ({
            x: new Date(soc.measuredAt).getTime(),
            y: soc.sensorValue,
        }));
    }

    private _getChartXAxisRange() {
        const now = Date.now();
        const twelveHoursInMs = 12 * 60 * 60 * 1000;

        return {
            xaxis: {
                type: 'datetime',
                min: now - twelveHoursInMs,
                max: now + twelveHoursInMs,
            },
        };
    }

    private _hasNoStateOfChargeData(stateOfChargeData: VehicleStateOfChargeData): boolean {
        return (stateOfChargeData?.history ?? []).length === 0 &&
            (stateOfChargeData?.prognosis ?? []).length === 0;
    }
}
